import { z } from 'zod';
import {
  COMMAND_SLOT_MODES,
  dutyPointPlaceSchema,
  calendarDateSchema,
  requirementLinesInputSchema,
  requirementLineSchema,
} from './arrangement.schema';
import { createPaginationSchema, objectIdSchema } from './common.schema';
import { FILL_STATES } from '../constants/fillState.constant';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE DEPLOYMENT — the atomic unit (PA_REQUIREMENTS §4.2).
 *
 *  ⚠️ ONE DEPLOYMENT IS "PA × LOCATION SET × SLOT", UNIQUE ON THAT TRIPLE.
 *
 *  Requirement, allocation, assignment, command and duty events all hang off one of
 *  these. The arrangement above it is the ORDER — the printed artifact, the thing shown
 *  together to an officer — and it carries only what is genuinely shared.
 *
 *  ⚠️ A SEQUENTIAL ROUTE IS ONE DEPLOYMENT WITH N LOCATIONS, NOT N DEPLOYMENTS.
 *  This is §4.2's flagged deviation from a literal reading of "PA + Location + Slot is
 *  unique", and the reason is arithmetic: modelling a route as N deployments would
 *  triple-count the same men. One team, one force pool, one Mark Reached per stop.
 *
 *  ⚠️ EVERYTHING HERE IS STAMPED, AND STAMPED MEANS COPIED.
 *  A deployment does not read through to its arrangement for its requirement — it holds
 *  its own copy, so a detached exception can differ and so P4's fulfilment maths never
 *  has to ask which of two sources is current. `isDetached` is what protects a copy that
 *  a human deliberately changed.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export const DEPLOYMENT_STATUSES = ['PENDING', 'ALLOCATED', 'ACTIVE', 'CLOSED', 'CANCELLED'] as const;
export type DeploymentStatus = (typeof DEPLOYMENT_STATUSES)[number];

/**
 * ⚠️ [LIFECYCLE] "A DUTY UNDER THIS ORDER HAS BEEN CARRIED OUT", AND IT IS THE HONEST
 * ANSWER TO A REQUIREMENT THAT CANNOT BE MET LITERALLY.
 *
 * FR-5.2.14 blocks deletion "once the duty window has opened OR ANY DUTY EVENT EXISTS".
 * There is no DutyEvent in this system: PA_REQUIREMENTS §4 declares
 * `DutyEvent[] (START_DUTY | REACHED | STAND_DOWN)` and the phase table puts it in P5 —
 * no model, no collection, no service, no route. Writing a guard against a collection that
 * does not exist would be a guard that never fires, dressed as one that does.
 *
 * So the seam is these two statuses, which are DECLARED and have NO WRITER — exactly as
 * `ALLOCATED` was declared and unwritten until P4 wrote it. They are the states a duty
 * reaches once it is happening on the ground, and P5 is what will write them.
 *
 * ⚠️ AND THE CAVEAT BELONGS HERE RATHER THAN IN A CHANGELOG: TODAY THIS GUARD REFUSES
 * NOTHING THE WINDOW GUARD DID NOT. A duty cannot be `ACTIVE` before its window opens, so
 * `hasDutyEvent ⟹ hasOpenedWindow` in this release. It is not redundant in PRINCIPLE —
 * FR-5.4.5's supervisor Mark Reached is one way the two come apart — but it is redundant
 * in practice now, and saying so is the difference between a seam and a decoration.
 * `dutyEventDeferred.test.ts` fires it by writing the status directly, so it is a guard
 * that is exercised today rather than a promise about P5.
 */
export const DEPLOYMENT_STATUSES_ON_THE_GROUND = ['ACTIVE', 'CLOSED'] as const satisfies readonly DeploymentStatus[];

/**
 * One command role resolved FOR ONE SLOT.
 *
 * ⚠️ THE FILLED ROW, NOT THE DEFINITION — see `arrangement.schema.ts`'s note. It carries
 * a `roleKey` and no label, because the label lives on the frozen policy version the
 * arrangement points at and therefore cannot drift out of step with it.
 */
export const deploymentCommandSchema = z.object({
  /** ⚠️ [amended D4.3] `null` on a one-off row, which carries its own name instead. */
  roleKey: z.string().nullable(),
  /**
   * ⚠️ [amended D4.3] THE ONE EXCEPTION TO "NO LABEL IS STORED", AND IT IS NOT ONE.
   * A one-off role has no policy version to hold its name, so the name IS the row. There is
   * still nothing to drift out of step with, which is what the rule above was protecting.
   */
  adHocLabel: z.string().nullable(),
  mode: z.enum(COMMAND_SLOT_MODES),
  officerIds: z.array(objectIdSchema),
  displayOrder: z.number().int().min(0),
});

export const deploymentSchema = z.object({
  id: objectIdSchema,
  arrangementId: objectIdSchema,
  /** The BUSINESS day. An overnight shift belongs to the day it STARTED. */
  dutyDate: calendarDateSchema,
  slotIndex: z.number().int().min(0),
  slotKey: z.string(),
  slotLabel: z.string(),
  shiftName: z.string().nullable(),
  /** `false` for a whole-day duty. Rendering asks this; window arithmetic asks the instants. */
  hasFixedTime: z.boolean(),
  spansMidnight: z.boolean(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  /** Length 1 when concurrent; the ordered route when sequential. */
  locations: z.array(dutyPointPlaceSchema),
  /** ⚠️ A human decided this one differs. Every re-stamp SKIPS it. */
  isDetached: z.boolean(),
  requirementLines: z.array(requirementLineSchema),
  commands: z.array(deploymentCommandSchema),
  status: z.enum(DEPLOYMENT_STATUSES),
  stampedAt: z.iso.datetime(),
  archivedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

/** The strip and the detail list. Narrower, because a PA can carry five hundred of these. */
export const deploymentListItemSchema = z.object({
  id: objectIdSchema,
  arrangementId: objectIdSchema,
  /**
   * ⚠️ [amended D4] WHICH DUTY POINT PRODUCED THIS DUTY. The detail page groups by it, and
   * without it a three-card order's duty list is one flat wall of rows with no way to tell
   * which card each came from.
   */
  dutyPointId: z.string(),
  dutyPointTitle: z.string(),
  dutyDate: calendarDateSchema,
  slotIndex: z.number().int().min(0),
  slotKey: z.string(),
  slotLabel: z.string(),
  hasFixedTime: z.boolean(),
  spansMidnight: z.boolean(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime(),
  locationNames: z.array(z.string()),
  /**
   * ⚠️ [C3a §6] THE DUTY'S OWN STAMPED REPORTING INSTANT — REQUIRED, NULLABLE.
   *
   * §6: where a duty has no shift but DOES have a reporting time, every surface says
   * `From 3:00 PM` rather than `No fixed time`. The renderer needs the value to say it, and
   * every row renders a time either way — so this is [P6d]'s required-key-nullable half rather
   * than its optional one: `null` answers "this duty has no reporting time", which is a fact
   * the cell has to render something for.
   *
   * ⚠️ IT IS THE DUTY'S, NEVER THE CARD'S. A client could join back to
   * `dutyPoints[].reportingTime` through `dutyPointId` and would be wrong for a DETACHED duty,
   * which carries the instant it was stamped with while the card has moved on.
   */
  reportingAt: z.iso.datetime().nullable(),
  /**
   * [STAND-DOWN] THE DUTY'S OWN STAMPED STAND-DOWN INSTANT — required, nullable, for `reportingAt`'s
   * reasons. Set only on a whole-day duty; a shift duty's end is `endsAt`.
   */
  standDownAt: z.iso.datetime().nullable(),

  isDetached: z.boolean(),
  status: z.enum(DEPLOYMENT_STATUSES),
  /** ⚠️ FORCE ONLY. There is no command count on a deployment, here or anywhere. */
  requiredCount: z.number().int().min(0),
  /**
   * ⚠️ THREE NUMBERS, THREE DIFFERENT TRUE STATEMENTS. [P4]
   *
   * `requiredCount` is what the order asks for, `allocatedCount` is how much the divisions
   * were asked to provide, and `namedCount` is how many people are actually against it.
   * P3a.1 shipped the first two and recorded that the second was "a true statement about the
   * order rather than a claim about people"; P4 adds the claim about people beside them
   * rather than redefining one of the others, because an order asking for 10, allocating 10
   * and having named 6 is a real order in mid-flight and all three numbers are worth seeing.
   */
  allocatedCount: z.number().int().min(0),
  namedCount: z.number().int().min(0),
  /** Derived from this duty's shares on every read — never stored (FR-5.3.6a). */
  fillState: z.enum(FILL_STATES),
  /**
   * ⚠️ [C5b §3] THE ROLL-UP HAS CARRIED IT SINCE C5 AND THIS DTO DROPPED IT. `toListItem` read
   * `FillCounts.fillState` and not `.closedShort`, so a duty every share had closed short at
   * nobody named read `All Filled` in the duty table.
   */
  closedShort: z.boolean(),
});

export const DEPLOYMENT_SORT_FIELDS = ['dutyDate', 'startsAt'] as const;
export type DeploymentSortField = (typeof DEPLOYMENT_SORT_FIELDS)[number];

export const listDeploymentsQuerySchema = createPaginationSchema(DEPLOYMENT_SORT_FIELDS, 'startsAt')
  .extend({
    arrangementId: objectIdSchema.optional(),
    status: z.enum(DEPLOYMENT_STATUSES).optional(),
    dutyDate: calendarDateSchema.optional(),
    /** [amended D4] "Show me only this card's duties" — the detail page's per-card view. */
    dutyPointId: z.string().trim().min(1).max(24).optional(),
    detachedOnly: z.stringbool().default(false),
  })
  .extend({ sortOrder: z.enum(['asc', 'desc']).default('asc') });

/**
 * Detaching one slot after publish, so every later re-stamp leaves it alone.
 *
 * The body is optional: "this one is different" is a meaningful act on its own, and an
 * operator who detaches before editing has said something true. Sending lines edits them
 * in the same call.
 */
export const detachDeploymentSchema = z.object({
  requirementLines: requirementLinesInputSchema.optional(),
});
